import { SettingsApi } from '@itsrever/rever-api'
import { apiClient } from '@/infrastructure/api'
import { NangoConnection } from '@/domain/nango-connection'
import {
  toGroupNangoConnections,
  toNangoConnectionHealth,
  toNangoConnectSession,
  toNangoConnections,
  toShopifyAppAutomationToken
} from './dto'

const settingsApi = new SettingsApi(undefined, undefined, apiClient)

// Connections made in the session belong to the group; link them to a store afterwards.
async function createConnectSession(
  group: string,
  allowedIntegrations?: string[]
) {
  const response = await settingsApi.createNangoConnectSession({
    pbCreateNangoConnectSessionRequest: {
      group,
      allowed_integrations: allowedIntegrations
    }
  })
  return toNangoConnectSession(response.data)
}

async function listConnections(ecommerceId: string, signal?: AbortSignal) {
  const response = await settingsApi.listNangoConnections(
    { ecommerceId },
    { signal }
  )
  return toNangoConnections(response.data.connections ?? [])
}

async function linkConnection(
  ecommerceId: string,
  connection: Pick<NangoConnection, 'integration' | 'connectionId'>
) {
  await settingsApi.linkNangoConnection({
    pbLinkNangoConnectionRequest: {
      ecommerce_id: ecommerceId,
      integration: connection.integration,
      connection_id: connection.connectionId
    }
  })
}

async function unlinkConnection(
  ecommerceId: string,
  connection: Pick<NangoConnection, 'integration' | 'connectionId'>
) {
  await settingsApi.unlinkNangoConnection({
    ecommerceId,
    integration: connection.integration,
    connectionId: connection.connectionId
  })
}

// Replaces the connection the store reads its platform credentials from; the server checks it is the store's,
// unless the merchant confirmed switching the store to the connection's.
async function assignPlatformConnection(
  ecommerceId: string,
  connection: Pick<NangoConnection, 'integration' | 'connectionId'>,
  switchStore = false
) {
  await settingsApi.assignNangoPlatformConnection({
    pbAssignNangoPlatformConnectionRequest: {
      ecommerce_id: ecommerceId,
      integration: connection.integration,
      connection_id: connection.connectionId,
      switch_store: switchStore
    }
  })
}

async function listGroupConnections(group: string, signal?: AbortSignal) {
  const response = await settingsApi.listGroupNangoConnections(
    { group },
    { signal }
  )
  return toGroupNangoConnections(response.data.connections ?? [])
}

// Deletes the credentials from Nango and unassigns them from every store of the group.
async function deleteConnection(
  group: string,
  connection: Pick<NangoConnection, 'integration' | 'connectionId'>
) {
  await settingsApi.deleteNangoConnection({
    group,
    integration: connection.integration,
    connectionId: connection.connectionId
  })
}

// Asks the store now whether it accepts the connection's credentials, and records the answer.
async function checkConnection(
  group: string,
  connection: Pick<NangoConnection, 'integration' | 'connectionId'>
) {
  const response = await settingsApi.checkNangoConnection({
    group,
    integration: connection.integration,
    connectionId: connection.connectionId,
    pbCheckNangoConnectionRequest: {}
  })
  return toNangoConnectionHealth(response.data.health)
}

// The session replaces the connection's credentials and keeps its id, so its stores need no reassigning.
async function createReconnectSession(
  group: string,
  connection: Pick<NangoConnection, 'integration' | 'connectionId'>
) {
  const response = await settingsApi.createNangoReconnectSession({
    group,
    integration: connection.integration,
    connectionId: connection.connectionId,
    pbCreateNangoReconnectSessionRequest: {}
  })
  return toNangoConnectSession(response.data)
}

async function findAppAutomationToken(
  ecommerceId: string,
  signal?: AbortSignal
) {
  const response = await settingsApi.findShopifyAppAutomationToken(
    { ecommerceId },
    { signal }
  )
  return toShopifyAppAutomationToken(response.data)
}

async function saveAppAutomationToken(
  ecommerceId: string,
  token: string,
  expiresAt: Date
) {
  const response = await settingsApi.saveShopifyAppAutomationToken({
    pbSaveShopifyAppAutomationTokenRequest: {
      ecommerce_id: ecommerceId,
      token,
      expires_at: expiresAt.toISOString()
    }
  })
  return toShopifyAppAutomationToken(response.data)
}

export default {
  findAppAutomationToken,
  saveAppAutomationToken,
  createConnectSession,
  listConnections,
  linkConnection,
  unlinkConnection,
  assignPlatformConnection,
  listGroupConnections,
  deleteConnection,
  checkConnection,
  createReconnectSession
}
